/* Per-browser (not per-account) flags for the Face ID flow. A passkey is bound to THIS device's
   platform authenticator, not to "the user" in the abstract — so whether to auto-offer/auto-try
   it is inherently a client-side, per-browser question, unlike everything else auth-related in
   this app (which is server-authoritative). localStorage failures (private mode, quota) degrade
   silently to "treat as not registered / not dismissed" — the worst case is the prompt asks
   again, never a crash. */

const REGISTERED_KEY = 'tt-webauthn-registered';
const DISMISSED_KEY = 'tt-webauthn-dismissed';

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    localStorage.setItem(key, 'true');
  } catch {
    // Non-fatal — see module doc comment.
  }
}

export function isWebauthnRegisteredOnThisDevice(): boolean {
  return readFlag(REGISTERED_KEY);
}

export function markWebauthnRegistered(): void {
  writeFlag(REGISTERED_KEY);
}

export function isWebauthnRegistrationDismissed(): boolean {
  return readFlag(DISMISSED_KEY);
}

export function dismissWebauthnRegistration(): void {
  writeFlag(DISMISSED_KEY);
}

/* @simplewebauthn/browser re-throws a cancelled/dismissed ceremony wrapped in its own
   WebAuthnError class (see identifyAuthenticationError/identifyRegistrationError), NOT the raw
   DOMException — the wrapper still carries `.name = 'NotAllowedError'` (WebAuthnError's
   constructor defaults `name` to the original error's), so checking `instanceof DOMException`
   never matches and a plain dismiss gets misreported as a real failure. Match on `.name` alone. */
export function isUserCancelledWebauthnError(err: unknown): boolean {
  return typeof err === 'object' && err !== null && 'name' in err && err.name === 'NotAllowedError';
}
